import DeleteIcon from '@mui/icons-material/Delete';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import {
  Box,
  CircularProgress,
  Stack,
} from '@mui/material';
import { PaperPlaneTilt } from 'phosphor-react';
import React from 'react';
import { Button } from 'src/UIKit';
import { ChatComposerPanel } from './ChatComposerPanel.styles';
import { IconActionButton } from './IconActionButton';
import BannedWordsModal from 'src/components/BannedWordsModal/view';
import AttachFileUrlModal from 'src/components/AttachFileUrlModal/view';
import ChatFileIcon from 'src/components/ChatFileIcon/view';
import TemplateText from 'src/components/TemplateText/TemplateText';
import { MessageCreditEstimates } from 'src/components/MessageCreditEstimates';
import { useChatFooterController } from '../../controllers';
import { ChatFooterProps } from '../../types';
import ScheduleMessageDialog from '../ScheduleMessageDialog';

const Footer = (props: ChatFooterProps) => {
  const {
    ConfirmationDialog,
    message,
    messageTemplates,
    defaultCustomFields,
    newMessage,
    url,
    mimeType,
    isLoading,
    isMessageValid,
    isScheduleDialogOpen,
    dateScheduled,
    showbannedWordsModal,
    isContactInvalid,
    isContactOptedOut,
    isActionDisabled,
    isCompanyHasSubscription,
    isCompanyInSandBoxMode,
    showAttachFileModal,
    setAttachFileModal,
    sendButtonRef,
    toolbarContainer,
    setToolbarContainer,
    setIsMessageValid,
    setDateScheduled,
    handleTextUpdate,
    validateBlockListAndSave,
    openScheduleDialog,
    handleScheduleDialogClose,
    handleScheduleMessage,
    handleRemoveAttachment,
    handleAttachFile,
    handleEditorBlur,
    handleBannedWordsConfirm,
    handleBannedWordsCancel,
    handleSendClick,
    handleFocus,
    isMobile,
  } = useChatFooterController(props);

  return (
    <Box position="relative">
      <ChatComposerPanel>
        {url && (
          <Stack direction="row" justifyContent="start" alignItems="center" p={1} pb={0}>
            <ChatFileIcon url={url} mimeType={mimeType} width="10%" />
            <Button
              disabled={!(isCompanyHasSubscription || isCompanyInSandBoxMode)}
              onClick={handleRemoveAttachment}
            >
              <DeleteIcon />
            </Button>
          </Stack>
        )}

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          px={1}
          pt={1}
          pb={0.5}
        >
          <MessageCreditEstimates
            viewType="inbox"
            messageType={newMessage?.links?.length ? 'mms' : 'sms'}
            messageText={message}
            hasAttachments={!!newMessage?.links?.length}
            onValidationChange={setIsMessageValid}
          />
          <Box ref={setToolbarContainer} display="flex" alignItems="center" />
        </Stack>

        <Stack direction="row" alignItems="flex-end" spacing={1} px={0.5} pb={0.5}>
          <Stack width="100%">
            {defaultCustomFields && defaultCustomFields.length > 0 && (
              <Box bgcolor="background.paper" borderRadius={1.5}>
                <TemplateText
                  text={message}
                  messageTemplates={messageTemplates}
                  emodjiPickerPosition={{ top: '-450px', right: '0' }}
                  customFields={defaultCustomFields}
                  disabled={false}
                  onEnterPress={(newText: string) => {
                    if (isMobile) {
                      validateBlockListAndSave(false, newText);
                    } else {
                      if (sendButtonRef.current) {
                        sendButtonRef.current.click();
                      }
                    }
                  }}
                  updateText={handleTextUpdate}
                  showConfirmationText={false}
                  confirmationText=""
                  setFocus={true}
                  minHeight="initial"
                  height="initial"
                  padding="8px 12px"
                  showAttachFile={true}
                  onAttachFile={() => setAttachFileModal(true)}
                  externalToolbar
                  toolbarContainer={toolbarContainer}
                  hasError={!isMessageValid}
                  handleFocus={() => handleFocus?.(true)}
                  handleBlur={handleEditorBlur}
                />
              </Box>
            )}
          </Stack>

          {showAttachFileModal && (
            <AttachFileUrlModal
              show={showAttachFileModal}
              setShowModal={setAttachFileModal}
              handleFileChange={handleAttachFile}
            />
          )}

          <IconActionButton
            ref={sendButtonRef}
            aria-label="Send message"
            disabled={isActionDisabled}
            onClick={handleSendClick}
          >
            {isLoading && !(isContactInvalid || isContactOptedOut) ? (
              <CircularProgress size={24} color="secondary" />
            ) : (
              <PaperPlaneTilt size={24} weight="fill" />
            )}
          </IconActionButton>
          <IconActionButton
            aria-label="Schedule message"
            disabled={isActionDisabled}
            onClick={openScheduleDialog}
          >
            <ScheduleSendIcon fontSize="small" />
          </IconActionButton>
        </Stack>
      </ChatComposerPanel>

      <ScheduleMessageDialog
        open={isScheduleDialogOpen}
        value={dateScheduled}
        onClose={handleScheduleDialogClose}
        onChange={setDateScheduled}
        onConfirm={handleScheduleMessage}
      />

      <BannedWordsModal
        isOpen={showbannedWordsModal}
        onConfirm={handleBannedWordsConfirm}
        onCancel={handleBannedWordsCancel}
      />
      <ConfirmationDialog />
    </Box>
  );
};

export default Footer;
